import { z } from 'zod';
import createEnv from '../../env/createEnv';

const { env, getEnv, getEnvOrFail } = createEnv({
  server: {
    NODE_VERSION: z.string().optional(),

    // NEW RELIC
    NEW_RELIC_APP_NAME: z.string().optional(),
    NEW_RELIC_LICENSE_KEY: z.string().optional(),

    // MONARCH - FEATURE FLAG SERVICE
    MONARCH_SOURCE_ID: z.string().optional(),
    MONARCH_ENDPOINT: z.string().optional(),
    MONARCH_TOKEN: z.string().optional(),
  },
  client: {
    // CLOUDINARY
    NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME: z.string().optional(),

    // DATADOG RUM (REAL USER MONITORING)
    NEXT_PUBLIC_DD_APPLICATION_ID: z.string(),
    NEXT_PUBLIC_DD_CLIENT_TOKEN: z.string(),
    NEXT_PUBLIC_DD_ENV_NAME: z.string(),
    NEXT_PUBLIC_DD_RUM_VERSION: z.string(),
    NEXT_PUBLIC_DD_SERVICE_NAME: z.string(),
    NEXT_PUBLIC_DD_SESSION_REPLAY_SAMPLE_RATE: z.string(),
    NEXT_PUBLIC_DD_SITE_URL: z.string(),

    // COMMERCE COORDINATOR
    NEXT_PUBLIC_EDX_CC_ORDER_HISTORY_PATH: z.string().optional(),
    NEXT_PUBLIC_EDX_CC_ORDER_RECEIPT_PATH: z.string(),
    NEXT_PUBLIC_EDX_CC_SDN_FAILURE_PATH: z.string().optional(),
    NEXT_PUBLIC_EDX_SDN_FAILURE_PAGE_PATH: z.string().optional(),
    NEXT_PUBLIC_EDX_COMMERCE_COORDINATOR_BASE_URL: z.string(),

    // EDX ECOMMERCE
    NEXT_PUBLIC_EDX_ECOMMERCE_BASKET_ADD_PATH: z.string().optional(),
    NEXT_PUBLIC_EDX_ECOMMERCE_COUPON_REDEEM_PATH: z.string().optional(),

    // EDX LEGACY ECOMMERCE
    NEXT_PUBLIC_EDX_LEGACY_ECOM_BASE_URL: z.url().optional(),

    // AUTHENTICATION (EDX)
    NEXT_PUBLIC_EDX_JWT_PAYLOAD_COOKIE_NAME: z.string(),
    NEXT_PUBLIC_EDX_JWT_SIGNATURE_COOKIE_NAME: z.string(),
    NEXT_PUBLIC_EDX_LOGIN_REFRESH_ENDPOINT: z.string(),
    NEXT_PUBLIC_EDX_LOGIN_ENDPOINT: z.url().optional(),
    NEXT_PUBLIC_EDX_USER_LOCATION_COOKIE_NAME: z.string().optional(),

    // EDX URLS
    NEXT_PUBLIC_EDX_LEARNER_DASHBOARD_URL: z.string(),
    NEXT_PUBLIC_EDX_LMS_BASE_URL: z.url(),
    NEXT_PUBLIC_EDX_LMS_PROGRAM_DASHBOARD_PATH: z.string(),
    NEXT_PUBLIC_EDX_PAYMENT_FAQS_URL: z.url().optional(),
    NEXT_PUBLIC_EDX_SUPPORT_URL: z.url().optional(),
    NEXT_PUBLIC_EDX_SITE_URL: z.url().optional(),

    // FEATURE FLAGS (migration phases - edx-style gradual rollout)
    NEXT_PUBLIC_ENABLE_DYNAMIC_CURRENCY: z.enum(['true', 'false']).optional(),
    NEXT_PUBLIC_FEATURE_TESTING: z.string().optional(), // will be removed after the tests are done
    NEXT_PUBLIC_FEATURE_API_ROUTES: z.enum(['true', 'false']).optional(),
    NEXT_PUBLIC_FEATURE_SESSION_MGMT: z.enum(['true', 'false']).optional(),
    NEXT_PUBLIC_FEATURE_ROUTER: z.enum(['true', 'false']).optional(),

    // MONARCH - FEATURE FLAG SERVICE
    NEXT_PUBLIC_MONARCH_AUTO_REQUEST_ENABLED: z
      .enum(['true', 'false'])
      .optional(),

    // FRONTASTIC CONFIGURATION
    NEXT_PUBLIC_EXT_BUILD_ID: z.string(),
    NEXT_PUBLIC_FRONTASTIC_HOST: z.url(),

    // GOOGLE TAG MANAGER
    NEXT_PUBLIC_GTM_CONTAINER_ID: z.string(),

    // LEARNING BASE URL
    NEXT_PUBLIC_LEARNING_BASE_URL: z.url().optional(),

    // ONETRUST - COOKIE CONSENT MANAGEMENT
    NEXT_PUBLIC_ONE_TRUST_APPLICATION_ID: z.string(),

    // PAYPAL
    NEXT_PUBLIC_PAYPAL_CLIENT_ID: z.string().optional(),
    NEXT_PUBLIC_ENABLE_PAYPAL_FEATURE: z.enum(['true', 'false']).optional(),

    // TAGULAR - ANALYTICS TRACKING
    NEXT_PUBLIC_TAGULAR_SOURCE_KEY: z.string().optional(),
    NEXT_PUBLIC_TAGULAR_WRITE_KEY: z.string().optional(),
  },
  runtimeEnv: {
    NEXT_PUBLIC_DD_APPLICATION_ID: process.env.NEXT_PUBLIC_DD_APPLICATION_ID,
    NEXT_PUBLIC_DD_CLIENT_TOKEN: process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN,
    NEXT_PUBLIC_DD_ENV_NAME: process.env.NEXT_PUBLIC_DD_ENV_NAME,
    NEXT_PUBLIC_DD_RUM_VERSION: process.env.NEXT_PUBLIC_DD_RUM_VERSION,
    NEXT_PUBLIC_DD_SERVICE_NAME: process.env.NEXT_PUBLIC_DD_SERVICE_NAME,
    NEXT_PUBLIC_DD_SESSION_REPLAY_SAMPLE_RATE:
      process.env.NEXT_PUBLIC_DD_SESSION_REPLAY_SAMPLE_RATE,
    NEXT_PUBLIC_DD_SITE_URL: process.env.NEXT_PUBLIC_DD_SITE_URL,
    NEXT_PUBLIC_EDX_CC_ORDER_HISTORY_PATH:
      process.env.NEXT_PUBLIC_EDX_CC_ORDER_HISTORY_PATH,
    NEXT_PUBLIC_EDX_CC_ORDER_RECEIPT_PATH:
      process.env.NEXT_PUBLIC_EDX_CC_ORDER_RECEIPT_PATH,
    NEXT_PUBLIC_EDX_CC_SDN_FAILURE_PATH:
      process.env.NEXT_PUBLIC_EDX_CC_SDN_FAILURE_PATH,
    NEXT_PUBLIC_EDX_COMMERCE_COORDINATOR_BASE_URL:
      process.env.NEXT_PUBLIC_EDX_COMMERCE_COORDINATOR_BASE_URL,
    NEXT_PUBLIC_EDX_JWT_PAYLOAD_COOKIE_NAME:
      process.env.NEXT_PUBLIC_EDX_JWT_PAYLOAD_COOKIE_NAME,
    NEXT_PUBLIC_EDX_JWT_SIGNATURE_COOKIE_NAME:
      process.env.NEXT_PUBLIC_EDX_JWT_SIGNATURE_COOKIE_NAME,
    NEXT_PUBLIC_EDX_USER_LOCATION_COOKIE_NAME:
      process.env.NEXT_PUBLIC_EDX_USER_LOCATION_COOKIE_NAME,
    NEXT_PUBLIC_EDX_LEARNER_DASHBOARD_URL:
      process.env.NEXT_PUBLIC_EDX_LEARNER_DASHBOARD_URL,
    NEXT_PUBLIC_EDX_LMS_BASE_URL: process.env.NEXT_PUBLIC_EDX_LMS_BASE_URL,
    NEXT_PUBLIC_EDX_LMS_PROGRAM_DASHBOARD_PATH:
      process.env.NEXT_PUBLIC_EDX_LMS_PROGRAM_DASHBOARD_PATH,
    NEXT_PUBLIC_EDX_LOGIN_ENDPOINT: process.env.NEXT_PUBLIC_EDX_LOGIN_ENDPOINT,
    NEXT_PUBLIC_EDX_LOGIN_REFRESH_ENDPOINT:
      process.env.NEXT_PUBLIC_EDX_LOGIN_REFRESH_ENDPOINT,
    NEXT_PUBLIC_EDX_PAYMENT_FAQS_URL:
      process.env.NEXT_PUBLIC_EDX_PAYMENT_FAQS_URL,
    NEXT_PUBLIC_EDX_SUPPORT_URL: process.env.NEXT_PUBLIC_EDX_SUPPORT_URL,
    NEXT_PUBLIC_ENABLE_DYNAMIC_CURRENCY:
      process.env.NEXT_PUBLIC_ENABLE_DYNAMIC_CURRENCY,
    NEXT_PUBLIC_FEATURE_TESTING: process.env.NEXT_PUBLIC_FEATURE_TESTING, // will be removed after the tests are done
    NEXT_PUBLIC_FEATURE_API_ROUTES: process.env.NEXT_PUBLIC_FEATURE_API_ROUTES,
    NEXT_PUBLIC_FEATURE_SESSION_MGMT:
      process.env.NEXT_PUBLIC_FEATURE_SESSION_MGMT,
    NEXT_PUBLIC_FEATURE_ROUTER: process.env.NEXT_PUBLIC_FEATURE_ROUTER,
    NEXT_PUBLIC_MONARCH_AUTO_REQUEST_ENABLED:
      process.env.NEXT_PUBLIC_MONARCH_AUTO_REQUEST_ENABLED,
    NEXT_PUBLIC_EXT_BUILD_ID: process.env.NEXT_PUBLIC_EXT_BUILD_ID,
    NEXT_PUBLIC_FRONTASTIC_HOST: process.env.NEXT_PUBLIC_FRONTASTIC_HOST,
    NEXT_PUBLIC_GTM_CONTAINER_ID: process.env.NEXT_PUBLIC_GTM_CONTAINER_ID,
    NEXT_PUBLIC_LEARNING_BASE_URL: process.env.NEXT_PUBLIC_LEARNING_BASE_URL,
    NEXT_PUBLIC_ONE_TRUST_APPLICATION_ID:
      process.env.NEXT_PUBLIC_ONE_TRUST_APPLICATION_ID,
    NEXT_PUBLIC_PAYPAL_CLIENT_ID: process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID,
    NEXT_PUBLIC_TAGULAR_SOURCE_KEY: process.env.NEXT_PUBLIC_TAGULAR_SOURCE_KEY,
    NEXT_PUBLIC_TAGULAR_WRITE_KEY: process.env.NEXT_PUBLIC_TAGULAR_WRITE_KEY,
  },
});

export { env, getEnv, getEnvOrFail };
